import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { addTransaction, updateTransaction } from "@/services/financeService";
import { todayISO, formatMoney } from "@/utils/format";
import { budgetStatus, chargeCoversCycle, chargeHasAnyCycle, chargeMonthlyFor } from "@/utils/charges";
import type { Charge, ChargeCategory, Cycle, FinanceCategory, Transaction, TransactionType } from "@/types";
import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import type { PaymentMethod } from "@/types";
import { currencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  type: TransactionType;
  categories: FinanceCategory[];
  cycles: Cycle[];
  charges?: Charge[];
  chargeCategories?: ChargeCategory[];
  editing?: Transaction | null;
}

export default function TransactionModal({
  open,
  onClose,
  onSaved,
  type,
  categories,
  cycles,
  charges = [],
  chargeCategories = [],
  editing,
}: Props) {
  const { notify } = useToast();
  const [categoryId, setCategoryId] = useState("");
  const [chargeId, setChargeId] = useState("");
  const [form, setForm] = useState({
    amount: "",
    date: todayISO(),
    cycleId: "",
    description: "",
    method: "ESPECES" as PaymentMethod,
  });
  const [busy, setBusy] = useState(false);

  const cycleIds = cycles.map((c) => c.id);
  const activeCharges = charges.filter((c) => c.isActive && chargeHasAnyCycle(c, cycleIds));
  const selectedCharge = charges.find((c) => c.id === chargeId);
  const availableCycles = selectedCharge ? cycles.filter((c) => chargeCoversCycle(selectedCharge, c.id)) : cycles;
  const provisional = chargeMonthlyFor(selectedCharge, form.cycleId);
  const status = budgetStatus(Number(form.amount) || 0, provisional);
  const blocked = type === "depense" && status === "over";

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setCategoryId(editing.categoryId ?? "");
      setChargeId(editing.chargeId ?? "");
      setForm({
        amount: String(editing.amount),
        date: editing.date,
        cycleId: editing.cycleId,
        description: editing.description,
        method: editing.method ?? "ESPECES",
      });
      return;
    }
    if (type === "recette") {
      const firstCat = categories.find((c) => c.type === type);
      setCategoryId(firstCat?.id ?? "");
      setChargeId("");
      setForm({ amount: "", date: todayISO(), cycleId: firstCat?.cycleId ?? cycles[0]?.id ?? "", description: "", method: "ESPECES" });
    } else {
      const first = charges.find((c) => c.isActive && chargeHasAnyCycle(c, cycleIds));
      setCategoryId("");
      setChargeId(first?.id ?? "");
      const cycle = first ? cycles.find((c) => chargeCoversCycle(first, c.id)) : undefined;
      setForm({ amount: "", date: todayISO(), cycleId: cycle?.id ?? cycles[0]?.id ?? "", description: "", method: "ESPECES" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, categories, cycles, type, charges]);

  // Recette : le cycle suit la catégorie choisie.
  const setCategory = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    setCategoryId(id);
    setForm((p) => ({ ...p, cycleId: cat ? cat.cycleId : p.cycleId }));
  };

  // Dépense : le cycle se cale sur le premier cycle couvert par la charge.
  const setCharge = (id: string) => {
    const charge = charges.find((c) => c.id === id);
    setChargeId(id);
    setForm((p) => {
      const cycle = charge ? cycles.find((c) => chargeCoversCycle(charge, c.id)) : undefined;
      return { ...p, cycleId: cycle?.id ?? p.cycleId };
    });
  };

  const handleSave = async () => {
    if (type === "recette") {
      if (!categoryId) {
        notify("Veuillez choisir une catégorie.", "error");
        return;
      }
    } else {
      if (!chargeId) {
        notify("Veuillez choisir une charge.", "error");
        return;
      }
      if (!form.cycleId) {
        notify("Veuillez choisir le cycle de cette dépense.", "error");
        return;
      }
    }
    if (!form.amount || Number(form.amount) <= 0) {
      notify("Veuillez saisir un montant valide.", "error");
      return;
    }
    if (blocked) {
      notify("Le montant dépasse le prévisionnel de la charge.", "error");
      return;
    }

    setBusy(true);
    const payload = {
      type,
      categoryId: type === "recette" ? categoryId : "",
      chargeId: type === "depense" ? chargeId : undefined,
      amount: Number(form.amount),
      date: form.date,
      cycleId: form.cycleId,
      description: form.description || (type === "recette" ? "Recette" : "Dépense"),
      method: type === "recette" ? form.method : undefined,
    };
    try {
      if (editing) {
        await updateTransaction(editing.id, payload);
        notify("Opération mise à jour.");
      } else {
        await addTransaction(payload);
        notify(type === "recette" ? "Recette enregistrée." : "Dépense enregistrée.");
      }
      onSaved();
      onClose();
    } catch (err) {
      // Ex. 409 : la charge a déjà sa dépense pour ce mois dans ce cycle.
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  const catOptions = categories.filter((c) => c.type === type);

  const banner =
    type === "depense" && status !== "none" && provisional > 0
      ? {
          safe: {
            cls: "border-primary-200 bg-primary-50 text-primary-800",
            icon: "ri-checkbox-circle-line",
            text: "Montant bien en-dessous du prévisionnel — marge confortable.",
          },
          close: {
            cls: "border-accent-200 bg-accent-50 text-accent-800",
            icon: "ri-alert-line",
            text: `Attention : vous vous approchez du prévisionnel (${formatMoney(provisional)} / mois).`,
          },
          over: {
            cls: "border-accent-600 bg-accent-600 text-background-50",
            icon: "ri-error-warning-line",
            text: `Dépassement : ${formatMoney(Number(form.amount) || 0)} excède le prévisionnel (${formatMoney(provisional)}). Réduisez le montant pour enregistrer.`,
          },
        }[status]
      : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier l'opération" : type === "recette" ? "Enregistrer une recette" : "Enregistrer une dépense"}
      icon={type === "recette" ? "ri-arrow-up-circle-line" : "ri-arrow-down-circle-line"}
      subtitle={type === "depense" ? "Choisissez une charge : sa catégorie et son prévisionnel se remplissent automatiquement" : undefined}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy || blocked}>
            {busy ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      {type === "recette" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Catégorie" required value={categoryId} onChange={(e) => setCategory(e.target.value)}>
            <option value="">— Choisir —</option>
            {cycles.map((c) => (
              <optgroup key={c.id} label={c.label}>
                {catOptions
                  .filter((cat) => cat.cycleId === c.id)
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </Select>
          <Select label="Cycle" value={form.cycleId} onChange={(e) => setForm({ ...form, cycleId: e.target.value })}>
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <Input
            label={`Montant (${currencySymbol()})`}
            type="number"
            required
            placeholder="Ex : 1500000"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
          <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Select
            label="Mode d'encaissement"
            value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value as PaymentMethod })}
            hint="Compté dans votre caisse du jour"
            wrapperClass="sm:col-span-2"
          >
            {(Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[]).map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABEL[m]}
              </option>
            ))}
          </Select>
          <Textarea
            label="Description"
            rows={2}
            placeholder="Détail de l'opération..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            wrapperClass="sm:col-span-2"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select label="Charge" required value={chargeId} onChange={(e) => setCharge(e.target.value)} wrapperClass="sm:col-span-2">
              <option value="">— Choisir une charge —</option>
              {chargeCategories.map((cat) => {
                const items = activeCharges.filter((c) => c.categoryId === cat.id);
                if (items.length === 0) return null;
                return (
                  <optgroup key={cat.id} label={cat.name}>
                    {items.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </Select>

            <Select
              label="Cycle de la dépense"
              required
              value={form.cycleId}
              onChange={(e) => setForm({ ...form, cycleId: e.target.value })}
              hint="Une dépense porte sur un seul cycle à la fois."
            >
              {availableCycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>

            <div className="flex flex-col justify-end">
              <span className="mb-1.5 block text-xs font-medium text-foreground-700">Prévisionnel mensuel</span>
              <span className="rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-800">
                {provisional > 0 ? formatMoney(provisional) : "Aucun prévisionnel"}
              </span>
            </div>

            <Input
              label={`Montant réel (${currencySymbol()})`}
              type="number"
              required
              placeholder="Ex : 1500000"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              wrapperClass="sm:col-span-2"
            />
          </div>

          {banner && (
            <div className={`flex items-start gap-2.5 rounded-md border px-3.5 py-2.5 text-sm ${banner.cls}`}>
              <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                <i className={`${banner.icon} text-base`}></i>
              </span>
              <span className="leading-snug">{banner.text}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Textarea
              label="Description"
              rows={2}
              placeholder="Détail de la dépense..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              wrapperClass="sm:col-span-2"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}